import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DataService } from './data.service';
import { matchPreLoginSlugFromRouterUrl, WhiteLabelService } from './white-label.service';

export interface WhatsappBubbleState {
  show: boolean;
  e164Number: string | null;
  buildingName: string | null;
}

/**
 * Resuelve y expone la burbuja de soporte por WhatsApp según contexto:
 *
 * - **MTS** (pre-login): endpoint publico MTS (sin cambios).
 * - **White-label** con telefono en ecosystem.support: numero del edificio.
 * - **Munily global**: endpoint `munily-dashboard/support-whatsapp`.
 */
@Injectable()
export class SupportWhatsappService implements OnDestroy {
  private whatsappStateSubject = new BehaviorSubject<WhatsappBubbleState>({
    show: false,
    e164Number: null,
    buildingName: null,
  });
  private routerSub: Subscription | null = null;
  private buildingSub: Subscription | null = null;
  private mtsSupportPhoneCache: string | null | undefined = undefined;
  private munilySupportPhoneCache: string | null | undefined = undefined;

  constructor(
    private dataService: DataService,
    private whiteLabelService: WhiteLabelService,
    private router: Router,
    private http: HttpClient,
  ) {}

  init(): void {
    if (this.routerSub) return;

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.apply());

    this.buildingSub = this.dataService.buildingChanged$().subscribe(() => this.apply());

    this.apply();
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.buildingSub?.unsubscribe();
  }

  apply(): void {
    this.updateWhatsappState(this.dataService.getBuilding());
  }

  whatsappState$(): Observable<WhatsappBubbleState> {
    return this.whatsappStateSubject.asObservable();
  }

  /** Abre WhatsApp con el numero resuelto para el contexto actual. */
  openSupportChat(prefillText?: string): void {
    const state = this.whatsappStateSubject.getValue();
    if (!state.e164Number) {
      return;
    }
    const phone = state.e164Number.replace(/[^\d]/g, '');
    if (!phone) {
      return;
    }
    const url = prefillText
      ? `https://wa.me/${phone}?text=${encodeURIComponent(prefillText)}`
      : `https://wa.me/${phone}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private updateWhatsappState(building: any): void {
    if (this.isMtsPreLoginUrl()) {
      this.resolveMtsSupportPhone();
      return;
    }

    const buildingE164 = building
      ? this.whiteLabelService.getBuildingWhatsappE164(building)
      : null;

    if (buildingE164) {
      this.whatsappStateSubject.next({
        show: true,
        e164Number: buildingE164,
        buildingName: building?.name || null,
      });
      return;
    }

    if (!this.isLegacyMtsUrl()) {
      this.resolveMunilyGlobalSupportPhone();
      return;
    }

    this.whatsappStateSubject.next({ show: false, e164Number: null, buildingName: null });
  }

  private resolveMtsSupportPhone(): void {
    const requestUrl = `${environment.newApiBackend}/api/public/general-config/clients/mts/support-whatsapp`;

    if (this.mtsSupportPhoneCache !== undefined) {
      this.emitPhone(this.mtsSupportPhoneCache);
      return;
    }

    this.whatsappStateSubject.next({ show: false, e164Number: null, buildingName: null });

    this.http.get<unknown>(requestUrl).subscribe({
      next: (res) => {
        const payload = this.unwrapMonoApiBody<{ e164Number: string | null }>(res);
        const e164 = this.normalizePhone(payload?.e164Number);
        this.mtsSupportPhoneCache = e164;

        if (this.isMtsPreLoginUrl()) {
          this.emitPhone(e164);
        }
      },
      error: () => {
        this.mtsSupportPhoneCache = null;
        if (this.isMtsPreLoginUrl()) {
          this.emitPhone(null);
        }
      },
    });
  }

  private resolveMunilyGlobalSupportPhone(): void {
    const requestUrl = `${environment.newApiBackend}/api/public/general-config/munily-dashboard/support-whatsapp`;

    if (this.munilySupportPhoneCache !== undefined) {
      this.emitPhone(this.munilySupportPhoneCache);
      return;
    }

    this.whatsappStateSubject.next({ show: false, e164Number: null, buildingName: null });

    this.http.get<unknown>(requestUrl).subscribe({
      next: (res) => {
        const payload = this.unwrapMonoApiBody<{ e164Number: string | null }>(res);
        const e164 = this.normalizePhone(payload?.e164Number);
        this.munilySupportPhoneCache = e164;

        if (!this.isMtsPreLoginUrl() && !this.isLegacyMtsUrl()) {
          this.emitPhone(e164);
        }
      },
      error: () => {
        this.munilySupportPhoneCache = null;
        if (!this.isMtsPreLoginUrl() && !this.isLegacyMtsUrl()) {
          this.emitPhone(null);
        }
      },
    });
  }

  private emitPhone(e164: string | null): void {
    if (!e164) {
      this.whatsappStateSubject.next({ show: false, e164Number: null, buildingName: null });
      return;
    }
    this.whatsappStateSubject.next({
      show: true,
      e164Number: e164,
      buildingName: null,
    });
  }

  private normalizePhone(value: string | null | undefined): string | null {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private unwrapMonoApiBody<T>(body: unknown): T | null {
    if (body == null || typeof body !== 'object') return null;
    const wrapped = body as { data?: T };
    if ('data' in wrapped && wrapped.data !== undefined) {
      return wrapped.data as T;
    }
    return body as unknown as T;
  }

  private isMtsPreLoginUrl(): boolean {
    return matchPreLoginSlugFromRouterUrl(this.router.url) === 'mts';
  }

  private isLegacyMtsUrl(): boolean {
    const url = window.location.pathname + window.location.hash;
    return url.includes('/mts/');
  }
}
